import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

/** A confirmation of one frozen server read, not an execution authority. */
export default function OvernightConfirmation({ confirmation, onCancel, onConfirm }) {
  const panelRef = useRef(null);
  const cancelRef = useRef(null);
  useEffect(() => {
    const origin = confirmation.origin;
    cancelRef.current?.focus();
    const keepFocusInside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) cancelRef.current?.focus();
    };
    document.addEventListener('focusin', keepFocusInside);
    return () => {
      document.removeEventListener('focusin', keepFocusInside);
      if (origin?.isConnected) origin.focus();
    };
  }, [confirmation]);

  const finalisation = confirmation.action === 'finalisation';
  const validation = confirmation.action === 'validation';
  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onCancel();
      return;
    }
    if (event.repeat && ['Enter', ' '].includes(event.key)) event.preventDefault();
    if (event.key !== 'Tab') return;
    const controls = [...panelRef.current.querySelectorAll('button:not([disabled])')];
    const first = controls[0], last = controls[controls.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault(); last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault(); first.focus();
    }
  };
  return createPortal(<div className="foundry-dialog-backdrop" onMouseDown={(event) => {
    if (event.target === event.currentTarget) onCancel();
  }}>
    <div ref={panelRef} className="foundry-decision-dialog foundry-overnight-dialog" role="dialog"
      aria-modal="true" aria-labelledby="overnight-confirmation-title"
      aria-describedby="overnight-confirmation-description" data-testid={validation ? 'validation-confirmation' : 'overnight-confirmation'}
      onKeyDown={handleKeyDown}>
      <div className="foundry-dialog-heading"><div><span className="version">{validation ? confirmation.validationLabel : 'Single-use overnight operation'}</span>
        <h2 id="overnight-confirmation-title">Confirm {confirmation.title} {validation ? 'validation admission' : finalisation ? 'failed-assessment finalisation' : 'overnight admission'}</h2>
      </div></div>
      <p id="overnight-confirmation-description">{finalisation
        ? 'Record that synthesis did not run because upstream failed. Failed jobs, attempts, provisional research and accounting remain unchanged. No retry, provider call, reservation, fund release or next-track admission is performed.'
        : 'Admit the one existing authorised run for this track. This does not renew historical allowances or approve a product. Only explicit Confirm submits this frozen request.'}</p>
      <dl className="foundry-decision-envelope">
        <div><dt>Action</dt><dd>{validation ? 'Admit one validation run' : finalisation ? 'Finalise failed assessment' : 'Admit one overnight run'}</dd></div>
        <div><dt>Track</dt><dd>{confirmation.title} · {confirmation.trackId}</dd></div>
        <div><dt>Batch / owner reference</dt><dd>{confirmation.ownerReference}</dd></div>
        <div><dt>Existing operation</dt><dd>{confirmation.operationKey || `${confirmation.ownerReference}:${confirmation.trackId}`}</dd></div>
        {confirmation.runId && <div><dt>Existing run</dt><dd>{confirmation.runId}</dd></div>}
        <div><dt>Child allowance</dt><dd>{confirmation.childAllowance}</dd></div>
        <div><dt>{validation ? 'Task ceiling' : 'Batch ceiling'}</dt><dd>{confirmation.batchCeiling}</dd></div>
        <div><dt>Search / model limits</dt><dd>{confirmation.searchLimit} searches · {confirmation.modelLimit} model dispatches including permitted fallbacks</dd></div>
        <div><dt>Product boundary</dt><dd>Stop at HUMAN REVIEW. No DESIGN or downstream product execution.</dd></div>
        {validation && <div><dt>Source scope</dt><dd>{confirmation.sourceScope}</dd></div>}
        <div><dt>{finalisation ? 'Terminal proof' : 'Manifest seal'}</dt><dd>{confirmation.seal}</dd></div>
      </dl>
      <p className="foundry-muted">Opening or cancelling this dialog performs no admission or paid call. After submission, navigation or a client timeout does not cancel a server-side operation. Inspect this same identity if its outcome is unknown.</p>
      <div className="foundry-dialog-actions">
        <button ref={cancelRef} className="btn btn-outline foundry-touch" type="button" onClick={onCancel}>Cancel</button>
        <button className="btn btn-primary foundry-touch" type="button" onClick={onConfirm}>Confirm {finalisation ? 'finalisation' : 'admission'}</button>
      </div>
    </div>
  </div>, document.body);
}
